/**
 * RailGroupToolbar — group-level edit affordances rendered beside a group's
 * connector chip in the editable Rail view (Slice 3B.4): ⇄ flip connector,
 * ○/● toggle NOT, ⊟ ungroup. ⇄ shows only for groups with ≥2 children; ⊟ shows
 * only for explicit, non-negated groups. Analytics fired per action.
 *
 * HAVING parity (Plan 3 Task 5): `kind` (default `'where'`) parameterizes the
 * testids and the `feature:vqe_${kind}_group_edit` analytics, and threads
 * `clause` into the tree-op wrappers.
 */

import type { ReactElement } from 'react';

import type { BooleanNode } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { setGroupConnector } from '../../edit/edits/setGroupConnector';
import { toggleNegateNode } from '../../edit/edits/toggleNegateNode';
import { unwrapGroup } from '../../edit/edits/unwrapGroup';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { VqeTooltip } from '../common/VqeTooltip';

type GroupNode = BooleanNode & { kind: 'group' };

const BTN =
  'rounded px-1 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground hover:text-foreground';

export function RailGroupToolbar({
  node,
  path,
  editContext,
  kind = 'where',
}: {
  readonly node: GroupNode;
  readonly path: readonly number[];
  readonly editContext: VqeEditContext;
  readonly kind?: BooleanClauseTarget;
}): ReactElement {
  const pathKey = path.join('-') || 'root';
  const { track } = useAnalytics();
  const other: 'AND' | 'OR' = node.connector === 'AND' ? 'OR' : 'AND';
  const canFlip = node.children.length >= 2;
  const canUngroup = node.explicit && !node.negated;

  return (
    <div
      className="mt-1 flex flex-col items-center gap-0.5"
      data-testid={`vqe-${kind}-rail-group-${pathKey}-toolbar`}
    >
      {canFlip ? (
        <VqeTooltip content={`Switch to ${other}`} selfProvider>
          <button
            type="button"
            className={BTN}
            data-testid={`vqe-${kind}-rail-group-${pathKey}-flip`}
            aria-label={`Switch to ${other}`}
            onClick={() => {
              void setGroupConnector(path, other, toCommitCtx(editContext), kind);
              track(`feature:vqe_${kind}_group_edit`, { action: 'connector' });
            }}
          >
            ⇄
          </button>
        </VqeTooltip>
      ) : null}
      <VqeTooltip content={node.negated ? 'Remove NOT' : 'Negate (NOT)'} selfProvider>
        <button
          type="button"
          className={BTN}
          data-testid={`vqe-${kind}-rail-group-${pathKey}-negate`}
          aria-label={node.negated ? 'Remove NOT' : 'Negate (NOT)'}
          onClick={() => {
            void toggleNegateNode(path, toCommitCtx(editContext), kind);
            track(`feature:vqe_${kind}_group_edit`, { action: 'negate' });
          }}
        >
          {node.negated ? '●' : '○'}
        </button>
      </VqeTooltip>
      {canUngroup ? (
        <VqeTooltip content="Ungroup" selfProvider>
          <button
            type="button"
            className={BTN}
            data-testid={`vqe-${kind}-rail-group-${pathKey}-ungroup`}
            aria-label="Ungroup"
            onClick={() => {
              void unwrapGroup(path, toCommitCtx(editContext), kind);
              track(`feature:vqe_${kind}_group_edit`, { action: 'ungroup' });
            }}
          >
            ⊟
          </button>
        </VqeTooltip>
      ) : null}
    </div>
  );
}
